/* eslint-disable no-extend-native */
import React, { useState, useEffect, useRef, Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { utils, writeFileXLSX } from "xlsx";
import html2canvas from "html2canvas";

import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fr, is } from "date-fns/locale";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";
import moment from "moment"; // Ensure moment is imported if not already

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getStage } from "../../../../api/stages";
import { getAbsences } from "../../../../api/absences";
import { getSpecificUsers } from "api/users";

import CreateEditAbsences from "../../CreateEditAbsences";
import CreateEditStages from "../../../devicesComponents/CreateEditStages";

import { setCurrentDevices } from "../../../../redux/reducers/devicesReducer";
import { setCurrentOrder } from "../../../../redux/reducers/ordersReducer";
import { useDispatch } from "react-redux";

import EventBar from "./EventBar";

import xlsx from "../../../../assets/images/extension-de-format-de-fichier-xlsx-blanc.png";
import png from "../../../../assets/images/symbole-de-format-de-fichier-png-blanc.png";
import xlsxDark from "../../../../assets/images/extension-de-format-de-fichier-xlsx-bleu.png";
import pngDark from "../../../../assets/images/symbole-de-format-de-fichier-png-bleu.png";

const WORKER_BASE = (process.env.PUBLIC_URL || "") + "/workers";

// Memoized components
const MemoizedCreateEditAbsences = React.memo(CreateEditAbsences);
const MemoizedCreateEditStages = React.memo(CreateEditStages);

const LazyDataTable = React.lazy(() => import("examples/Tables/DataTable"));
const MemoizedDataTable = React.memo(LazyDataTable);

export default function Global() {
  useAuthCheck();
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDay, setStartDay] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [stages, setStages] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [mo, setMo] = useState([]);
  const [foremans, setForemans] = useState([]);
  const [userList, setUserList] = useState([]);
  const [currentDays, setCurrentDays] = useState([]);
  const [globalColumns, setGlobalColumns] = useState([]);
  const [globalRows, setGlobalRows] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isLoadingYear, setIsLoadingYear] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [absenceEditOpen, setAbsenceEditOpen] = useState(false);
  const [currentAbsence, setCurrentAbsence] = useState(null);
  const [missionEditOpen, setMissionEditOpen] = useState(false);
  const [currentMission, setCurrentMission] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [processedUsers, setProcessedUsers] = useState([]);

  const [controller] = useMaterialUIController();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { darkMode } = controller;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, index) => currentYear - 2 + index);
  const [exportStartMonth, setExportStartMonth] = useState(new Date(selectedYear, 0)); // January of the current year
  const [exportEndMonth, setExportEndMonth] = useState(new Date(selectedYear, 11)); // December of the current year

  const [scrollLeft, setScrollLeft] = useState(0);
  const [captureWidth, setCaptureWidth] = useState(0);

  Date.prototype.addDays = function (days) {
    var dat = new Date(this.valueOf());
    dat.setDate(dat.getDate() + days);
    return dat;
  };

  Date.prototype.removeDays = function (days) {
    var dat = new Date(this.valueOf());
    dat.setDate(dat.getDate() - days);
    return dat;
  };

  Date.prototype.addMonths = function (months) {
    var dat = new Date(this.valueOf());
    dat.setMonth(dat.getMonth() + months);
    return dat;
  };

  Date.prototype.removeMonths = function (months) {
    var dat = new Date(this.valueOf());
    dat.setMonth(dat.getMonth() - months);
    return dat;
  };

  Date.prototype.addYears = function (years) {
    var dat = new Date(this.valueOf());
    dat.setFullYear(dat.getFullYear() + years);
    return dat;
  };

  useEffect(() => {
    dispatch(setCurrentDevices([]));
    dispatch(setCurrentOrder(null));
  }, [dispatch]);

  const handleYearChange = useCallback((event, newValue) => {
    setSelectedYear(newValue);
    setIsLoadingYear(true);
  }, []);

  useEffect(() => {
    if (selectedYear) {
      const newStartDate = new Date(selectedYear, 0, 1);
      setStartDay(newStartDate);
      setExportStartMonth(new Date(selectedYear, 0));
      setExportEndMonth(new Date(selectedYear, 11));
      // Additional logic to rebuild the table...
    }
  }, [selectedYear]);

  useEffect(() => {
    console.log("🔧 Starting datesWorker for startDay:", startDay);
    const datezWorker = new Worker(WORKER_BASE + "/datesWorker.js");
    const startDate = startDay.toISOString();
    const endDate = startDay.addYears(1).removeDays(1).toISOString();
    console.log("📅 Sending dates to worker:", { startDate, endDate });
    datezWorker.postMessage([startDate, endDate]);

    datezWorker.onmessage = (e) => {
      if (e.data.error) {
        console.error(`❌ datesWorker error: ${e.data.error}`);
        // Handle the error appropriately in your UI
      } else {
        console.log("✅ datesWorker success, received currentDays:", e.data.length);
        setCurrentDays(e.data);
      }
    };

    datezWorker.onerror = (error) => {
      console.error(`❌ datesWorker error: ${error.message}`);
      // Handle the error appropriately in your UI
    };

    return () => datezWorker.terminate();
  }, [startDay]);

  useEffect(() => {
    let tempGlobalColumns = [{ Header: "", accessor: "users", align: "left" }];
    let currentMonth = startDay.getMonth();
    let monthColumn = {
      Header: getMonthName(currentMonth + 1),
      columns: [],
      colspan: 0,
      info: "planning month",
      align: "center",
    };
    let weekColumn = { columns: [], colspan: 0, weekStart: null, weekEnd: null, isOddWeek: false };
    let lastDayProcessed = 0;

    currentDays.forEach((dateString, index) => {
      const dateParts = dateString.split("/");
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const year = parseInt(dateParts[2], 10);
      const date = new Date(year, month - 1, day);
      const weekNumber = getISOWeekNumber(date);
      const isOddWeek = weekNumber % 2 !== 0;

      // When the month changes
      if (month !== currentMonth + 1) {
        if (weekColumn.Header) {
          monthColumn.columns.push({ ...weekColumn, colspan: weekColumn.colspan });
          weekColumn = { columns: [], colspan: 0 };
        }
        tempGlobalColumns.push({
          ...monthColumn,
          info: "planning month",
          align: "center",
          colspan: monthColumn.colspan,
        });
        currentMonth = month - 1;
        monthColumn = {
          Header: getMonthName(month),
          info: "planning month",
          align: "center",
          columns: [],
          colspan: 0,
        };
        lastDayProcessed = 0; // Reset last processed day at start of new month
      }

      // Calculate if new week should start
      if (!weekColumn.weekStart || day - lastDayProcessed > 2) {
        if (weekColumn.Header) {
          monthColumn.columns.push({ ...weekColumn, colspan: weekColumn.colspan });
        }
        const weekNumber = getISOWeekNumber(date);
        weekColumn = {
          Header: `S${weekNumber}`,
          info: "planning week",
          align: "center",
          /* id: `S${weekNumber}-${getMonthName(month)}-${year}`, */
          columns: [],
          colspan: 0,
          weekStart: date,
          weekEnd: null,
          isOddWeek: isOddWeek,
        };
      }

      // Add day to week
      weekColumn.columns.push({
        Header: day.toString(),
        accessor: dateString,
        info: "planning day",
        align: "center",
        isOddWeek: weekColumn.isOddWeek,
      });
      weekColumn.colspan++;
      monthColumn.colspan++;
      weekColumn.weekEnd = date;
      lastDayProcessed = day;

      // Handle the last iteration
      if (index === currentDays.length - 1) {
        if (weekColumn.Header) {
          monthColumn.columns.push({ ...weekColumn, colspan: weekColumn.colspan });
        }
        tempGlobalColumns.push({ ...monthColumn, colspan: monthColumn.colspan });
      }
    });

    console.log("📊 Setting globalColumns, length:", tempGlobalColumns.length);
    setGlobalColumns(tempGlobalColumns);
  }, [currentDays, startDay]);

  function getISOWeekNumber(date) {
    const targetDate = new Date(date.valueOf()); // Create a copy of the date object to avoid mutating the original date
    const dayNumber = (date.getDay() + 6) % 7; // Convert Sunday (0) to 6, make Monday (1) as 0
    targetDate.setDate(targetDate.getDate() - dayNumber + 3); // Shift to the nearest Thursday (current or previous)

    const firstDayOfYear = new Date(targetDate.getFullYear(), 0, 1);
    const firstDayShift = (firstDayOfYear.getDay() + 6) % 7; // Adjust so Monday is 0
    firstDayOfYear.setDate(
      firstDayOfYear.getDate() + (firstDayShift > 3 ? 7 - firstDayShift : -firstDayShift)
    ); // Shift to the nearest Thursday

    const weekNumber = Math.ceil(((targetDate - firstDayOfYear) / 86400000 + 1) / 7); // Calculate full weeks to the nearest Thursday
    return weekNumber;
  }

  function getMonthName(monthNumber) {
    const monthNames = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    return monthNames[monthNumber - 1];
  }

  async function fetchElements() {
    const stagesResponse = await getStage("nothing", false, true);
    const absencesResponse = await getAbsences("none");
    const moResponse = await getSpecificUsers("mo from global");
    const foremansResponse = await getSpecificUsers("foremans");
    setStages(stagesResponse.data);
    setAbsences(absencesResponse.data);
    setMo(moResponse.data);
    setForemans(foremansResponse.data);
  }

  useEffect(() => {
    if (
      user &&
      user.role !== "Admin" &&
      user.role !== "Direction" &&
      user.role !== "Chef d'agence" &&
      user.role !== "Pilote" &&
      user.role !== "Contremaître" &&
      user.role !== "Commercial" &&
      user.role !== "Assistante de gestion"
    ) {
      navigate("/accueil");
    } else {
      fetchElements();
    }
  }, []);

  useEffect(() => {
    if (stages.length > 0 && absences.length > 0 && mo.length > 0) {
      console.log("🔧 Starting rowsWorker with data:", { stages: stages.length, absences: absences.length, mo: mo.length });
      const rowzWorker = new Worker(WORKER_BASE + "/rowsWorkerFixed.js");
      rowzWorker.postMessage({ stages, absences, mo });

      rowzWorker.onmessage = (e) => {
        if (e.data.error) {
          console.error(`❌ rowsWorker error: ${e.data.error}`);
          // Handle the error appropriately in your UI
        } else {
          console.log("✅ rowsWorker success, received userList:", e.data.length);
          setUserList(e.data);
        }
      };

      rowzWorker.onerror = (error) => {
        console.error(`❌ rowsWorker error: ${error.message}`);
        // Handle the error appropriately in your UI
      };

      return () => rowzWorker.terminate();
    } else {
      console.log("⏳ Waiting for data - stages:", stages.length, "absences:", absences.length, "mo:", mo.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absences, stages, mo]);

  useEffect(() => {
    if (userList.length > 0 && currentDays.length > 0) {
      console.log("🔧 Starting eventBarWorker with data:", { userList: userList.length, currentDays: currentDays.length });
      const eventBarWorker = new Worker(WORKER_BASE + "/eventWorker.js");
      eventBarWorker.postMessage({ userList, currentDays });

      eventBarWorker.onmessage = (e) => {
        if (e.data.error) {
          console.error(`❌ eventBarWorker error: ${e.data.error}`);
          // Handle the error appropriately in your UI
        } else {
          console.log("✅ eventBarWorker success, received processedUsers:", e.data.length);
          setProcessedUsers(e.data);
        }
      };

      eventBarWorker.onerror = (error) => {
        console.error(`❌ eventBarWorker error: ${error.message}`);
        // Handle the error appropriately in your UI
      };

      return () => eventBarWorker.terminate();
    } else {
      console.log("⏳ Waiting for eventBar data - userList:", userList.length, "currentDays:", currentDays.length);
    }
  }, [userList, currentDays]);

  const generateRowData = (currentUser) => {
    let row = {
      users: (
        <MDBox lineHeight={1}>
          <MDTypography
            display="block"
            area={currentUser.userArea}
            role={currentUser.userRole}
            foremans={currentUser.userForemans}
            pl={2.5}
            pr={1}
            variant="button"
            fontWeight="medium"
          >
            {currentUser.userName}
          </MDTypography>
        </MDBox>
      ),
    };

    // Use Object.entries to iterate over processedEvents as [date, event] pairs
    Object.entries(currentUser.processedEvents || {}).forEach(([date, event]) => {
      // Construct a unique key for each EventBar component
      const key = `${currentUser.userId}-${date}-${event.eventIds}`;

      let label;

      if (event.conflictType === "Mission") {
        label = event.eventInfos[0].address;
      } else if (event.conflictType && event.conflictType.includes("mission")) {
        label = [];
        event.eventInfos.forEach((mission) => {
          label.push(mission.address);
        });
      }

      row[date] = (
        <EventBar
          stages={stages}
          absences={absences}
          setCurrentOrderId={setCurrentOrderId}
          setCurrentMission={setCurrentMission}
          setMissionEditOpen={setMissionEditOpen}
          setCurrentAbsence={setCurrentAbsence}
          setAbsenceEditOpen={setAbsenceEditOpen}
          currentDays={currentDays && currentDays}
          key={key}
          darkMode={darkMode}
          event={event.eventInfos}
          startDate={date}
          endDate={event.endDate}
          color={event.eventColor}
          scndLabel={label}
          label={
            event.conflictType === "Mission"
              ? event.eventInfos[0].address
              : event.conflictType === "Absence"
              ? event.eventInfos[0].description
              : event.conflictType
          }
          user={user && user}
          eventId={event.eventIds}
          conflictType={event.conflictType}
        />
      );
    });

    return row;
  };

  const overlayStyle = {
    position: "relative",
    height: "100%", // Adjust height as needed
  };

  const loadingOverlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: darkMode ? "rgba(32, 41, 64, 0.85)" : "rgba(255, 255, 255, 0.85)", // Semi-transparent white background
    color: darkMode ? "white" : "black",
    zIndex: 1000, // Ensure it's above the table
  };

  useEffect(() => {
    if (processedUsers.length > 0) {
      const rows = processedUsers.map(generateRowData);
      console.log("📋 Setting globalRows, length:", rows.length);
      setGlobalRows(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedUsers, darkMode]);

  const getEventColor = (eventType) => {
    if (eventType === "Mission") {
      return { fgColor: { rgb: "0082D8" } }; // Solid blue
    } else if (eventType === "Absence") {
      return { fgColor: { rgb: "F2DE1D" } }; // Solid yellow
    } else if (eventType?.includes("absence") && eventType?.includes("mission")) {
      return { fgColor: { rgb: "E01111" } }; // Solid red for mixed events
    } else if (eventType?.includes("missions")) {
      return { fgColor: { rgb: "E055CD" } }; // Default pink color for other types
    } else {
      return { fgColor: { rgb: "CCCCCC" } }; // Default gray color for other event types
    }
  };

  function getMonthArray(startMonth, endMonth) {
    const monthNames = [
      "janvier",
      "février",
      "mars",
      "avril",
      "mai",
      "juin",
      "juillet",
      "août",
      "septembre",
      "octobre",
      "novembre",
      "décembre",
    ];
    const startIndex = monthNames.indexOf(startMonth.toLowerCase());
    const endIndex = monthNames.indexOf(endMonth.toLowerCase());

    if (startIndex === -1 || endIndex === -1) {
      throw new Error("Invalid month name provided.");
    }

    if (startIndex <= endIndex) {
      return monthNames.slice(startIndex, endIndex + 1);
    } else {
      // Handle wrap-around scenario
      return [
        ...monthNames.slice(startIndex, monthNames.length),
        ...monthNames.slice(0, endIndex + 1),
      ];
    }
  }

  function exportDataTableToExcel(
    columns,
    rows,
    currentDays,
    exportStartMonth,
    exportEndMonth,
    selectedYear
  ) {
    const workbook = utils.book_new();
    const data = [];

    const startMonth = exportStartMonth.toLocaleString("fr-FR", { month: "long" });
    const endMonth = exportEndMonth.toLocaleString("fr-FR", { month: "long" });
    const months = getMonthArray(startMonth, endMonth);

    const headers = { months: [""], weeks: [""], days: [""] };
    let validDays = [];

    columns.forEach((column) => {
      if (column.info === "planning month" && months.includes(column.Header.toLowerCase())) {
        column.columns.forEach((week) => {
          headers.months.push(column.Header);
          headers.weeks.push(week.Header);
          week.columns.forEach((day) => {
            headers.days.push(day.Header);
            validDays.push(day.accessor);
            headers.months.push("");
            headers.weeks.push("");
          });
          headers.months.pop();
          headers.weeks.pop();
        });
      }
    });

    data.push(headers.months, headers.weeks, headers.days);

    const merges = [];
    const firstValidDayIndex = currentDays.indexOf(validDays[0]); // Calculate the index of the first valid day in the complete days list

    // Preparing each row
    rows.forEach((row, rowIndex) => {
      const rowData = [row.users.props.children.props.children]; // Assuming 'users' is directly the name
      let columnOffset = 1; // Start from 1 to account for the 'User' column

      columns.forEach((column) => {
        if (column.info === "planning month" && months.includes(column.Header.toLowerCase())) {
          column.columns.forEach((week) => {
            week.columns.forEach((day) => {
              const cellValue = row[day.accessor]?.props?.label || "";
              rowData.push(cellValue); // Add cell value directly

              // Check if event spans multiple days
              if (row[day.accessor]?.props?.startDate && row[day.accessor]?.props?.endDate) {
                const startIndex = currentDays.indexOf(row[day.accessor].props.startDate);
                const endIndex = currentDays.indexOf(row[day.accessor].props.endDate);
                if (endIndex > startIndex) {
                  merges.push({
                    s: { r: rowIndex + 3, c: columnOffset + (startIndex - firstValidDayIndex) }, // Adjust start index by subtracting the first valid day index
                    e: { r: rowIndex + 3, c: columnOffset + (endIndex - firstValidDayIndex) }, // Same adjustment for end index
                  });
                }
              }
            });
          });
        }
      });
      data.push(rowData);
    });

    const worksheet = utils.aoa_to_sheet(data);
    console.log("merges", merges);
    worksheet["!merges"] = merges;

    worksheet["!cols"] = [{ wch: 25 }, ...Array(headers.days.length).fill({ wch: 10 })];

    utils.book_append_sheet(workbook, worksheet, "Global Planning");
    writeFileXLSX(workbook, `planning_global_${selectedYear}_${startMonth}_à_${endMonth}.xlsx`);
  }

  const handleExportClick = () => {
    // Adjusting start month to the first working day of the month
    const adjustedStartMonth = moment(exportStartMonth).startOf("month");
    while (adjustedStartMonth.day() === 0 || adjustedStartMonth.day() === 6) {
      // Skip weekends
      adjustedStartMonth.add(1, "days");
    }

    // Adjusting end month to include the last working day of the month
    const adjustedEndMonth = moment(exportEndMonth).endOf("month");
    while (adjustedEndMonth.day() === 0 || adjustedEndMonth.day() === 6) {
      // Skip weekends
      adjustedEndMonth.subtract(1, "days");
    }

    const startMonthFormatted = adjustedStartMonth.format("DD/MM/YYYY");
    const endMonthFormatted = adjustedEndMonth.format("DD/MM/YYYY");

    const startDayIndex = currentDays.indexOf(startMonthFormatted);
    const endDayIndex = currentDays.indexOf(endMonthFormatted);

    const dayWidth = 37.5; // Width in pixels of each day

    // Ensure indices are found, otherwise default to starting and ending bounds
    const safeStartIndex = startDayIndex === -1 ? 0 : startDayIndex;
    const safeEndIndex = endDayIndex === -1 ? currentDays.length - 1 : endDayIndex;

    // Calculate the pixel offset to scroll to
    const calculatedScrollLeft = safeStartIndex * dayWidth;
    // Calculate the width of the capture area
    const calculatedCaptureWidth = (safeEndIndex - safeStartIndex + 1) * dayWidth + 275;

    // Perform scrolling
    document.getElementById("ressources-scheduler-container").scrollTo({
      left: calculatedScrollLeft,
      behavior: "smooth",
    });

    setTimeout(() => {
      setIsMoving(false); // Trigger the useEffect for export
    }, 2000); // Adjust timing based on actual scroll performance

    // Set a timeout to allow the scroll to complete before capturing
    setTimeout(() => {
      setScrollLeft(calculatedScrollLeft);
      setCaptureWidth(calculatedCaptureWidth);
      setIsExporting(true); // Reset the export state
    }, 300); // Adjust timing based on actual scroll performance
  };

  useEffect(() => {
    const exportSchedulerToPNG = async () => {
      if (!isMoving && isExporting) {
        const startMonth = exportStartMonth.toLocaleString("fr-FR", { month: "long" });
        const endMonth = exportEndMonth.toLocaleString("fr-FR", { month: "long" });

        const schedulerElement = document.getElementById("ressources-scheduler");
        const canvas = await html2canvas(schedulerElement, {
          width: captureWidth,
          x: scrollLeft,
          scrollX: -scrollLeft, // Adjust the scrolling to start at the calculated left position
          scrollY: 0,
        });
        const image = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");

        let link = document.createElement("a");
        link.download = `planning_global_${selectedYear}_${startMonth}_à_${endMonth}.png`;
        link.href = image;
        link.click();

        setIsExporting(false);
      }
    };

    if (!isMoving && isExporting) {
      exportSchedulerToPNG();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExporting, isMoving, captureWidth, scrollLeft]); // Use the state variables as dependencies to re-run effect

  useEffect(() => {
    console.log("🔍 DEBUG - Checking data ready state:");
    console.log("  - globalColumns length:", globalColumns.length);
    console.log("  - globalRows length:", globalRows.length);
    console.log("  - currentDays length:", currentDays.length);
    console.log("  - processedUsers length:", processedUsers.length);
    console.log("  - stages length:", stages.length);
    console.log("  - absences length:", absences.length);
    console.log("  - mo length:", mo.length);
    
    if (globalColumns.length > 0 && globalRows.length > 0) {
      console.log("✅ Setting isDataReady to true");
      setIsDataReady(true);
    } else {
      console.log("❌ Data not ready yet");
      setIsDataReady(false);
    }
    setIsLoadingYear(false);
  }, [globalColumns.length, globalRows.length, globalRows, currentDays, processedUsers.length, stages.length, absences.length, mo.length]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={1.5}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={1.5}
              px={2}
              variant="gradient"
              borderRadius="lg"
              sx={{
                background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
                boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
                verticalAlign: "middle",
              }}
            >
              <Grid
                sx={{
                  height: "40.7px !important",
                }}
                container
              >
                {user &&
                  (user.role === "Contremaître" ||
                    user.role === "Chef d'agence" ||
                    user.role === "Admin" ||
                    user.role === "Pilote") && (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        alignItems: "right",
                        justifyContent: "right",
                      }}
                    >
                      <CreateEditStages
                        user={user && user}
                        edit={false}
                        stages={stages}
                        setStages={setStages}
                        global={true}
                      />
                      <IconButton
                        size="medium"
                        disableRipple
                        color="inherit"
                        onClick={() => {
                          exportDataTableToExcel(
                            globalColumns,
                            globalRows,
                            currentDays,
                            exportStartMonth,
                            exportEndMonth,
                            selectedYear
                          );
                        }}
                      >
                        <Icon>
                          {darkMode ? (
                            <img alt="" src={xlsxDark} height={24} width={24} />
                          ) : (
                            <img alt="" src={xlsx} height={24} width={24} />
                          )}
                        </Icon>
                      </IconButton>
                      <IconButton
                        size="medium"
                        disableRipple
                        color="inherit"
                        onClick={() => {
                          setIsMoving(true); // Trigger the useEffect for export
                          handleExportClick();
                        }}
                      >
                        <Icon>
                          {darkMode ? (
                            <img alt="" src={pngDark} height={24} width={24} />
                          ) : (
                            <img alt="" src={png} height={24} width={24} />
                          )}
                        </Icon>
                      </IconButton>
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={fr}>
                        <DesktopDatePicker
                          views={["month"]}
                          label="De"
                          value={exportStartMonth}
                          onChange={(newValue) => {
                            setExportStartMonth(newValue);
                          }}
                          inputFormat="MMMM" // Use "MMMM yyyy" if you want to include the year
                          renderInput={(params) => (
                            <TextField
                              sx={{
                                marginLeft: 0.75,
                                ".MuiAutocomplete-root": {
                                  color: darkMode ? "inherit !important" : "white !important",
                                },
                                ".MuiButtonBase-root": {
                                  color: darkMode ? "inherit !important" : "white !important",
                                },
                                ".MuiOutlinedInput-input": {
                                  width: 70,
                                  paddingRight: 0,
                                  color: darkMode ? "inherit !important" : "white !important",
                                  fontSize: 0.9 + "rem",
                                },
                                /* ".MuiInputBase-formControl": {
                                  paddingRight: 35 + "px !important",
                                }, */
                                ".MuiAutocomplete-clearIndicator": {
                                  display: "none",
                                },
                                ".MuiInputLabel-root": {
                                  color: darkMode ? "inherit !important" : "white !important",
                                },
                              }}
                              size="small"
                              {...params}
                            />
                          )}
                        />
                        <DesktopDatePicker
                          views={["month"]}
                          label="À"
                          value={exportEndMonth}
                          onChange={(newValue) => {
                            setExportEndMonth(newValue);
                          }}
                          inputFormat="MMMM" // Use "MMMM yyyy" if you want to include the year
                          renderInput={(params) => (
                            <TextField
                              sx={{
                                marginLeft: 1.25,
                                ".MuiAutocomplete-root": {
                                  color: darkMode ? "inherit !important" : "white !important",
                                },
                                ".MuiButtonBase-root": {
                                  color: darkMode ? "inherit !important" : "white !important",
                                },
                                ".MuiOutlinedInput-input": {
                                  width: 70,
                                  paddingRight: 0,
                                  color: darkMode ? "inherit !important" : "white !important",
                                  fontSize: 0.9 + "rem",
                                },
                                /* ".MuiInputBase-formControl": {
                                  paddingRight: 35 + "px !important",
                                }, */
                                ".MuiAutocomplete-clearIndicator": {
                                  display: "none",
                                },
                                ".MuiInputLabel-root": {
                                  color: darkMode ? "inherit !important" : "white !important",
                                },
                              }}
                              size="small"
                              {...params}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                <Grid
                  item
                  sx={{
                    position: "absolute",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "left",
                  }}
                  xs={12}
                  md={12}
                  lg={12}
                >
                  <MDTypography
                    variant="h4"
                    style={{
                      textAlign: "left",
                      lineHeight: "2.4rem",
                      verticalAlign: "middle",
                    }}
                    color={darkMode ? "inherit" : "white"}
                  >
                    Planning global
                  </MDTypography>
                  <Autocomplete
                    value={selectedYear}
                    options={yearOptions}
                    getOptionLabel={(option) => option.toString()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          marginLeft: 1.25,
                          ".MuiAutocomplete-root": {
                            color: darkMode ? "inherit !important" : "white !important",
                          },
                          ".MuiButtonBase-root": {
                            color: darkMode ? "inherit !important" : "white !important",
                          },
                          ".MuiOutlinedInput-root": {
                            paddingTop: "0.625rem !important",
                            paddingBottom: "0.625rem !important",
                          },
                          ".MuiOutlinedInput-input": {
                            paddingTop: "0px !important",
                            paddingBottom: "0px !important",
                            color: darkMode ? "inherit !important" : "white !important",
                            fontSize: 0.9 + "rem",
                          },
                          ".MuiInputBase-formControl": {
                            paddingRight: 35 + "px !important",
                          },
                          ".MuiAutocomplete-clearIndicator": {
                            display: "none",
                          },
                          ".MuiInputLabel-root": {
                            color: darkMode ? "inherit !important" : "white !important",
                          },
                        }}
                        label="Année"
                        size="small"
                      />
                    )}
                    onChange={handleYearChange}
                  />
                </Grid>
              </Grid>
            </MDBox>
            <MDBox pt={0.75} pl={0}>
              <Grid container item xs={12} p={3}>
                {user && (user.role === "Admin" || user.role === "Pilote") && currentAbsence && (
                  <MemoizedCreateEditAbsences
                    edit
                    setAbsences={setAbsences}
                    absence={currentAbsence}
                    editOpen={absenceEditOpen}
                    setEditOpen={setAbsenceEditOpen}
                    global={true}
                  />
                )}
                {user &&
                  (user.role === "Contremaître" ||
                    user.role === "Chef d'agence" ||
                    user.role === "Admin" ||
                    user.role === "Pilote") &&
                  missionEditOpen && (
                    <MemoizedCreateEditStages
                      edit
                      setStages={setStages}
                      stages={currentMission}
                      currentOrderId={currentOrderId}
                      editOpen={missionEditOpen}
                      setEditOpen={setMissionEditOpen}
                      global={true}
                      renderStep={false}
                    />
                  )}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignContent: "center",
                    alignItems: "center",
                    flexDirection: "row",
                  }}
                >
                  <MDBox sx={{ textAlign: "center", display: "inline-block" }}>
                    <MDBox sx={{ display: "inline-block", verticalAalign: "middle" }}>
                      <Icon
                        fontSize="normal"
                        style={{ color: "#f2de1d", marginLeft: 15, paddingTop: 2 }}
                      >
                        not_interested
                      </Icon>
                      <MDTypography
                        component="a"
                        variant="body1"
                        color={darkMode ? "white" : "inherit"}
                        style={{
                          textAlign: "center",
                          padding: 10,
                        }}
                      >
                        Absence
                      </MDTypography>
                    </MDBox>
                    <MDBox sx={{ display: "inline-block", verticalAalign: "middle" }}>
                      <Icon
                        fontSize="normal"
                        style={{
                          color: "#0082d8",
                          marginLeft: 15,
                          paddingTop: 2,
                        }}
                      >
                        engineering
                      </Icon>
                      <MDTypography
                        component="a"
                        variant="body1"
                        color={darkMode ? "white" : "inherit"}
                        style={{
                          textAlign: "center",
                          padding: 10,
                        }}
                      >
                        Mission
                      </MDTypography>
                    </MDBox>
                    <MDBox sx={{ display: "inline-block", verticalAalign: "middle" }}>
                      <Icon
                        fontSize="normal"
                        style={{ color: "#e055cd", marginLeft: 15, paddingTop: 2 }}
                      >
                        engineering
                      </Icon>
                      <MDTypography
                        component="a"
                        variant="body1"
                        color={darkMode ? "white" : "inherit"}
                        style={{
                          textAlign: "center",
                          padding: 10,
                        }}
                      >
                        Plus d'une mission
                      </MDTypography>
                    </MDBox>
                    <MDBox sx={{ display: "inline-block", verticalAalign: "middle" }}>
                      <Icon
                        fontSize="normal"
                        style={{ color: "#e01111", marginLeft: 15, paddingTop: 2 }}
                      >
                        error_outlined
                      </Icon>
                      <MDTypography
                        component="a"
                        variant="body1"
                        color={darkMode ? "white" : "inherit"}
                        style={{
                          textAlign: "center",
                          padding: 10,
                        }}
                      >
                        Conflit entre une/des mission(s) et une absence
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>
              <MDBox
                sx={{
                  table: { borderCollapse: "separate" },
                  "div.MuiTableContainer-root": {
                    borderRadius: 0 + " !important",
                    "div.MuiBox-root": {
                      position: !isMoving && "sticky",
                      left: !isMoving && 0,
                    },
                  },
                  tableLayout: "fixed",
                  "th.MuiBox-root[rowspan]": {
                    padding: 0 + " !important",
                    borderRight: "0.0625rem solid #e5e5e5;",
                    borderTop: "0.0625rem solid #e5e5e5;",
                    borderBottom: "none",
                    "& div.MuiBox-root:not(:first-of-type)": {
                      maxWidth: 37.5 + "px !important",
                      minWidth: 37.5 + "px !important",
                    },
                    "&:first-of-type:has(input)": {
                      zIndex: !isMoving && 1000,
                      position: !isMoving && "sticky",
                      left: 0,
                      background: darkMode ? "#202940" : "white",
                      borderBottom: "none",
                      maxWidth: 275 + "px !important",
                      minWidth: 275 + "px !important",
                    },
                  },
                  "tr.MuiTableRow-root:last-of-type": {
                    "td.MuiBox-root": {
                      borderBottom: "0.0625rem solid #e5e5e5;",
                    },
                  },
                  "td.MuiBox-root": {
                    "&:first-of-type": {
                      borderTop: "0.0625rem solid #e5e5e5;",
                      borderBottom: "none",
                      zIndex: !isMoving && 1000,
                      position: !isMoving && "sticky",
                      background: darkMode ? "#202940" : "white",
                      left: 0,
                    },
                    borderTop: "0.0625rem solid #e5e5e5;",
                    borderBottom: "none",
                    padding: 0 + " !important",
                    maxWidth: 37.5 + "px !important",
                    minWidth: 37.5 + "px !important",
                    borderRight: "0.0625rem solid #e5e5e5;",
                  },
                  "tbody tr.MuiTableRow-root": { height: 47.5 + "px !important" },
                  "thead tr.MuiTableRow-root": {
                    height: 30 + "px !important",
                    "&:not(:first-of-type)": {
                      "th.MuiBox-root": {
                        maxWidth: 37.5 + "px !important",
                        minWidth: 37.5 + "px !important",
                      },
                    },
                  },
                }}
              >
                <MDBox sx={overlayStyle}>
                  <Suspense
                    fallback={
                      <Grid height={641.188} container alignItems="center" justifyContent="center">
                        <MDTypography variant="h5">Chargement du tableau...</MDTypography>
                      </Grid>
                    }
                  >
                    {isDataReady ? (
                      <MemoizedDataTable
                        table={{
                          columns: globalColumns,
                          rows: globalRows,
                        }}
                        foremans={foremans}
                        userList={userList}
                        isSorted={false}
                        canSearch
                        entriesPerPage={false}
                        showTotalEntries={true}
                        noEndBorder
                      />
                    ) : (
                      <Grid height={641.188} container alignItems="center" justifyContent="center">
                        <MDTypography variant="h5">Chargement du tableau...</MDTypography>
                      </Grid>
                    )}
                  </Suspense>
                  {isLoadingYear && (
                    <div style={loadingOverlayStyle}>
                      <LoadingComponent message="Chargement de l'année..." />
                    </div>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

// Loading component for better user experience during data fetching
function LoadingComponent({ message }) {
  return <MDTypography variant="h5">{message}</MDTypography>;
}
